/**
 * Console theme state — light "pencil" / dark "graphite", default follows
 * the OS (``prefers-color-scheme``) with an explicit user override
 * persisted to localStorage.
 *
 * The resolved theme is applied as classes on the console ROOT element
 * (``console-theme`` + optional ``dark``), which scopes both the console
 * semantic vars (see ``console.css``) and the repo-wide shadcn ``.dark``
 * vars for nested @ando/ui primitives. Radix portals render OUTSIDE that
 * root — portaled content (DialogContent) must stamp
 * ``useConsoleThemeClass()`` on itself.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Monitor, Moon, Sun } from "lucide-react";
import * as React from "react";

import { cn } from "@ando/ui";

import "./console.css";

export type ConsoleThemePreference = "light" | "dark" | "system";
export type ConsoleResolvedTheme = "light" | "dark";

const STORAGE_KEY = "ando.console.theme";
const MEDIA_QUERY = "(prefers-color-scheme: dark)";

interface ConsoleThemeContextValue {
  preference: ConsoleThemePreference;
  resolved: ConsoleResolvedTheme;
  setPreference: (preference: ConsoleThemePreference) => void;
}

const ConsoleThemeContext = React.createContext<ConsoleThemeContextValue | null>(null);

function readStoredPreference(): ConsoleThemePreference {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // Storage unavailable (private mode / Tauri edge) — fall back to system.
  }
  return "system";
}

function readSystemTheme(): ConsoleResolvedTheme {
  return window.matchMedia(MEDIA_QUERY).matches ? "dark" : "light";
}

export function ConsoleThemeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [preference, setPreferenceState] =
    React.useState<ConsoleThemePreference>(readStoredPreference);
  const [systemTheme, setSystemTheme] = React.useState<ConsoleResolvedTheme>(readSystemTheme);

  React.useEffect(() => {
    const mql = window.matchMedia(MEDIA_QUERY);
    const onChange = (e: MediaQueryListEvent): void => {
      setSystemTheme(e.matches ? "dark" : "light");
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const setPreference = React.useCallback((next: ConsoleThemePreference): void => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best-effort persistence only.
    }
  }, []);

  const resolved: ConsoleResolvedTheme = preference === "system" ? systemTheme : preference;

  const value = React.useMemo<ConsoleThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ConsoleThemeContext.Provider value={value}>{children}</ConsoleThemeContext.Provider>;
}

export function useConsoleTheme(): ConsoleThemeContextValue {
  const ctx = React.useContext(ConsoleThemeContext);
  if (!ctx) {
    throw new Error("useConsoleTheme must be used within <ConsoleThemeProvider>");
  }
  return ctx;
}

/**
 * Scope classes for the console root AND for any Radix-portaled surface
 * (DialogContent) that renders outside the root.
 */
export function useConsoleThemeClass(): string {
  const { resolved } = useConsoleTheme();
  return cn("console-theme", resolved === "dark" && "dark");
}

const THEME_OPTIONS: ReadonlyArray<{
  value: ConsoleThemePreference;
  icon: typeof Sun;
}> = [
  { value: "light", icon: Sun },
  { value: "dark", icon: Moon },
  { value: "system", icon: Monitor },
];

/**
 * Three-state segmented theme toggle (light / dark / system) —
 * radiogroup semantics, keyboard reachable, visible focus ring.
 */
export function ConsoleThemeToggle(): React.ReactElement {
  const { i18n } = useLingui();
  const { preference, setPreference } = useConsoleTheme();

  const labels: Record<ConsoleThemePreference, string> = {
    light: i18n._(msg`Tema chiaro`),
    dark: i18n._(msg`Tema scuro`),
    system: i18n._(msg`Tema di sistema`),
  };

  return (
    <fieldset className="con-glass-control flex items-center gap-0.5 rounded-sm border border-[color:var(--con-border)] bg-[color:var(--con-surface-sunken)] p-0.5">
      <legend className="sr-only">{i18n._(msg`Tema della console`)}</legend>
      {THEME_OPTIONS.map(({ value, icon: Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            aria-label={labels[value]}
            title={labels[value]}
            onClick={() => setPreference(value)}
            className={cn(
              "flex h-6 w-7 items-center justify-center rounded-[4px]",
              "transition-colors duration-150 ease-out",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--con-focus)]",
              active
                ? "bg-[color:var(--con-surface-raised)] text-[color:var(--con-accent-text)] shadow-[inset_0_0_0_1px_var(--con-border)]"
                : "text-[color:var(--con-on-surface-faint)] hover:text-[color:var(--con-on-surface)]",
            )}
          >
            <Icon size={13} strokeWidth={1.75} aria-hidden />
          </button>
        );
      })}
    </fieldset>
  );
}
